<script lang="ts">
  import type { Grade, Run } from '../../../shared/contract'
  import Sign from './Sign.svelte'
  import { GRADES, GRADE_NAME, STATUSES, dockCounts, type Filter, type Status } from '../tally'

  /**
   * The filter, under the thumb: status (one at a time, with an explicit All)
   * over the four grades (any number; none means all). Each status carries its
   * count for the chosen grades, each grade its count for the chosen status.
   */
  let {
    runs,
    filter,
    desk = false,
    onstatus,
    ongrade,
  }: {
    runs: readonly Run[]
    filter: Filter
    desk?: boolean
    onstatus: (status: Status | null) => void
    ongrade: (grade: Grade) => void
  } = $props()

  const counts = $derived(dockCounts(runs, filter))
  const label = (s: Status | null) => s ?? 'All'
  /** A status nothing on the mountain has (open or groomed before the season) can't be chosen. */
  const none = $derived({
    open: !runs.some((r) => r.open),
    groomed: !runs.some((r) => r.groomed),
  })
</script>

<nav class="dock" class:desk aria-label="Filter runs">
  <div class="seg state" role="group" aria-label="Run state">
    {#each [null, ...STATUSES] as s (s ?? 'all')}
      {@const n = counts.status(s)}
      <button
        class:open={s === 'open'}
        class:closed={s === 'closed'}
        class:groomed={s === 'groomed'}
        aria-pressed={filter.status === s}
        disabled={(s === 'open' && none.open) || (s === 'groomed' && none.groomed)}
        aria-label="{label(s)}: {n} runs"
        onclick={() => onstatus(s)}
      >
        <span class="n">{n}</span><span class="k">{label(s)}</span>
      </button>
    {/each}
  </div>
  <div class="seg grades" class:some={filter.grades.length > 0} role="group" aria-label="Grade">
    {#each GRADES as g (g)}
      {@const n = counts.grade(g)}
      <button
        aria-pressed={filter.grades.includes(g)}
        aria-label="{GRADE_NAME[g]}: {n} runs"
        onclick={() => ongrade(g)}
      >
        <Sign grade={g} /><span class="c">{n}</span>
      </button>
    {/each}
  </div>
</nav>

<style>
  .dock { flex: none; margin-top: var(--gap); border: 1px solid #b1c0c6; background: #ffffff; border-radius: 1px; padding: 2px; display: flex; flex-direction: column; gap: 1px; }
  .seg { display: flex; min-width: 0; gap: 3px; }
  button { flex: 1 1 0; min-width: 44px; min-height: 44px; border-radius: 1px; display: flex; align-items: center; justify-content: center; color: var(--ink-2); white-space: nowrap; }
  button:hover:not(:disabled) { background: #eaf0f2; }
  .state button { flex-direction: column; gap: 0; height: 44px; border-bottom: 2px solid #9cadb6; background: #f0f4f5; }
  .state button.open { border-color: #08775f; }
  .state button.closed { border-color: #bc263c; }
  .state button.groomed { border-color: #0875b6; }
  .state button[aria-pressed='true'] { background: #294554; color: #ffffff; }
  .state button.open[aria-pressed='true'] { background: #08775f; }
  .state button.closed[aria-pressed='true'] { background: #bc263c; }
  .state button.groomed[aria-pressed='true'] { background: #0875b6; }
  .state button[aria-pressed='true'] .n { color: inherit; }
  .n { font-size: 16px; line-height: 19px; font-weight: 650; color: var(--ink); }
  .k { font: 650 11px/14px var(--font); letter-spacing: .055em; text-transform: uppercase; }
  .state button:disabled { opacity: .4; }
  .grades { border-top: 1px solid var(--line); padding-top: 2px; }
  .grades button { gap: 8px; height: 44px; font-size: 14px; }
  .grades button[aria-pressed='true'] { background: #dbe9ef; box-shadow: inset 0 0 0 1px #9cb8c5; }
  .c { color: var(--ink-2); }
  .grades.some button[aria-pressed='false'] :global(.g) { opacity: .45; }
  .grades.some button[aria-pressed='false'] .c { color: var(--ink-3); }
  @media (max-width: 999px) and (max-height: 760px) {
    .state button { height: 44px; }
    .n { font-size: 16px; line-height: 19px; }
  }
  @media (max-width: 400px) { .k { letter-spacing: .025em; } }
  .desk { margin: 0 0 0 auto; flex-direction: row; align-items: stretch; gap: 3px; }
  .desk .state button { min-width: 88px; height: 44px; padding: 0 6px; flex-direction: row; gap: 5px; }
  .desk .state button.groomed { min-width: 98px; }
  .desk .grades { border-top: 0; padding-top: 0; border-left: 1px solid var(--line); padding-left: 3px; }
  .desk .grades button { height: 44px; min-width: 54px; padding: 0 6px; }
  @media (min-width: 640px) {
    .dock:not(.desk) { flex-direction: row; }
    .dock:not(.desk) .grades { border-top: 0; border-left: 1px solid var(--line); padding: 0 0 0 3px; }
    .dock:not(.desk) .seg { flex: 1; }
  }
  @container board (max-width: 750px) {
    .desk { flex: 1 1 100%; margin: 0; flex-direction: column; gap: 2px; }
    .desk .state button { min-width: 44px; }
    .desk .grades { border-left: 0; padding-left: 0; border-top: 1px solid var(--line); padding-top: 2px; }
    .desk .grades button { min-width: 44px; height: 44px; }
  }
</style>
